import { useState, useEffect, useMemo, useRef } from "react";
import * as XLSX from "xlsx";
import PropTypes from "prop-types";

// Agregar estas importaciones al inicio del archivo
import escoService from "services/escoService";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Modal component
import MDModal from "components/MDModal";
import DetallesCandidato from "./components/DetallesCandidato";
import BulkUploadResultModal from "./components/BulkUploadResultModal"; 
import MDDeleteModal from "components/MDDeleteModal"; // Importar el modal de eliminación

// Custom Pagination component
import Pagination from "components/common/pagination/Pagination";

// Services
import candidateService from "services/candidatoService";

// Context
import { useMaterialUIController } from "context";

const ITEMS_PER_PAGE = 5;

// Función para convertir números de serie de Excel a fechas
const excelDateToJSDate = (excelDate) => {
  // Si ya es una fecha válida, devolverla
  if (typeof excelDate === 'string' && excelDate.includes('-')) {
    return excelDate;
  }

  // Si es un número (serial de Excel)
  if (typeof excelDate === 'number') {
    // Excel cuenta desde 1900-01-01, pero considera erróneamente 1900 como año bisiesto
    // Por eso restamos 25569 días (diferencia entre 1900-01-01 y 1970-01-01)
    const jsDate = new Date((excelDate - 25569) * 86400 * 1000);

    // Formatear como YYYY-MM-DD
    return jsDate.toISOString().split('T')[0];
  }

  // Si es una fecha de JavaScript
  if (excelDate instanceof Date) {
    return excelDate.toISOString().split('T')[0];
  }

  return excelDate;
};

// Función para procesar los datos de Excel antes de enviarlos
const processExcelData = (jsonData) => {
  return jsonData.map(row => {
    // Crear una copia del objeto
    const processedRow = { ...row };

    // Convertir la fecha de nacimiento si existe
    if (processedRow['Fecha Nacimiento']) {
      processedRow['Fecha Nacimiento'] = excelDateToJSDate(processedRow['Fecha Nacimiento']);
    }

    return processedRow;
  });
};

// Componente para renderizar el texto de las celdas
const TextCell = ({ value }) => (
  <MDTypography variant="caption" fontWeight="medium">
    {value || ""}
  </MDTypography>
);
TextCell.propTypes = {
  value: PropTypes.any,
};

// Componente para las acciones de cada fila
const ActionsCell = ({ candidateId, onEdit, onDelete, onViewDetails }) => (
  <MDBox display="flex" justifyContent="center" alignItems="center">
    <MDButton
      iconOnly
      color="success"
      size="small"
      sx={{ mr: 1 }}
      onClick={() => onViewDetails(candidateId)}
    >
      <Icon>visibility</Icon>
    </MDButton>
    <MDButton
      iconOnly
      color="info"
      size="small"
      sx={{ mr: 1 }}
      onClick={() => onEdit(candidateId)}
    >
      <Icon>edit</Icon>
    </MDButton>
    <MDButton
      iconOnly
      color="error"
      size="small"
      onClick={() => onDelete(candidateId)}
    >
      <Icon>delete</Icon>
    </MDButton>
  </MDBox>
);
ActionsCell.propTypes = {
  candidateId: PropTypes.string.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func.isRequired,
};

function CargarCandidatos() {
  const [controller] = useMaterialUIController();
  const { searchTerm } = controller;

  // Ref para el formulario individual
  const formRef = useRef(null);

  const [tableData, setTableData] = useState({ columns: [], rows: [] });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    color: "success",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [allCandidates, setAllCandidates] = useState([]);

  // Estado para el modal de detalles
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  // Nuevos estados para el modal de eliminación
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState(null);

  const [bulkUploadResults, setBulkUploadResults] = useState(null);
  const [resultsModalOpen, setResultsModalOpen] = useState(false);

  // Agregar estos nuevos estados después de los existentes
  const [suggestedSkills, setSuggestedSkills] = useState([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [skillsError, setSkillsError] = useState(null);
  const [suggestedOccupations, setSuggestedOccupations] = useState([]); // Nuevo estado para ocupaciones sugeridas

  // Estado para el formulario individual - Campos actualizados según el JSON
  const [formData, setFormData] = useState({
    nombreCompleto: "",
    numIdentificacion: "",
    correo: "",
    fechaNacimiento: "",
    edad: "",
    genero: null, // Cambiado de "" a null
    direccion: "",
    nivelEducativo: null, // Cambiado de "" a null
    experienciaLaboral: null, // Cambiado de "" a null
    habilidadesDestacadas: [],
    motivacionCargo: "",
    cargoAplicado: null, // Cambiado de "" a null
    ubicacionGeografica: "",
    pais: "Colombia",
  });
  const [editingCandidateId, setEditingCandidateId] = useState(null);

  // Configurar columnas de la tabla
  const tableColumns = [
    { Header: "Nombre", accessor: "nombre", width: "15%" },
    { Header: "Documento", accessor: "documento", width: "10%" },
    { Header: "Email", accessor: "email", width: "15%" },
    { Header: "Edad", accessor: "edad", width: "8%" },
    { Header: "Género", accessor: "genero", width: "10%" },
    { Header: "Nivel Educativo", accessor: "nivel_educativo", width: "12%" },
    { Header: "Experiencia", accessor: "experiencia_laboral", width: "10%" },
    { Header: "Cargo", accessor: "cargo_aplicado", width: "12%" },
    { Header: "Acciones", accessor: "acciones", width: "8%" },
  ];

  useEffect(() => {
    loadCandidates();
  }, []);

  const loadCandidates = async () => {
    try {
      setLoading(true);
      const candidates = await candidateService.getAllCandidates();
      // Ordenar candidatos por updated_at de forma descendente para mostrar los más recientes primero
      const sortedCandidates = candidates.sort((a, b) => {
        const dateA = new Date(a.updated_at);
        const dateB = new Date(b.updated_at);
        return dateB.getTime() - dateA.getTime();
      });
      setAllCandidates(sortedCandidates);
    } catch (error) {
      showNotification(error.message || "Error al cargar candidatos", "error");
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, color = "success") => {
    setNotification({
      open: true,
      message,
      color,
    });
  };

  const closeNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  // Función para calcular la edad basada en la fecha de nacimiento
  const calculateAge = (birthDate) => {
    if (!birthDate) return "";

    const today = new Date();
    const birth = new Date(birthDate);

    // Verificar que la fecha sea válida
    if (isNaN(birth.getTime())) return "";

    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    // Ajustar si el cumpleaños no ha ocurrido este año
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    // Validar que la edad sea positiva y razonable
    return age >= 0 && age <= 120 ? age.toString() : "";
  };

  // useEffect para calcular automáticamente la edad cuando cambie la fecha de nacimiento
  useEffect(() => {
    if (formData.fechaNacimiento) {
      const calculatedAge = calculateAge(formData.fechaNacimiento);
      setFormData(prev => ({ ...prev, edad: calculatedAge }));
    }
  }, [formData.fechaNacimiento]);

  // Modificar el handleInputChange para manejar la fecha de nacimiento
  const handleInputChange = (field, value) => {
    // Validación especial para número de identificación
    if (field === "numIdentificacion") {
      const numericValue = value.replace(/\D/g, "");
      if (numericValue.length <= 10) {
        setFormData(prev => ({ ...prev, [field]: numericValue }));
      }
      return;
    }

    // Para fecha de nacimiento, solo actualizar el campo - useEffect se encargará de la edad
    if (field === "fechaNacimiento") {
      setFormData(prev => ({ ...prev, [field]: value }));
      return;
    }

    // Para edad, solo permitir cambios manuales si no hay fecha de nacimiento
    if (field === "edad") {
      if (!formData.fechaNacimiento) {
        setFormData(prev => ({ ...prev, [field]: value }));
      }
      return;
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Función mejorada para manejar cambio de cargo
  const handleCargoChange = async (newCargo) => {
    handleInputChange("cargoAplicado", newCargo);
    
    console.log(`handleCargoChange - newCargo:`, newCargo);
    
    // Limpiar habilidades destacadas al cambiar el cargo
    setFormData(prev => ({ ...prev, habilidadesDestacadas: [] }));
    
    if (newCargo && newCargo.trim()) {
      setLoadingSkills(true);
      setSkillsError(null);
      
      console.log(`Cargo seleccionado: "${newCargo}"`);
      
      try {
        // Obtener habilidades de ESCO API
        const skillsData = await escoService.getSkillsForPosition(newCargo.trim());
        
        console.log('📊 Respuesta de ESCO:', skillsData);
        
        if (skillsData && skillsData.skills && skillsData.skills.length > 0) {
          // Extraer solo los títulos de las habilidades
          const skillTitles = skillsData.skills.map(skill => skill.title).filter(Boolean);
          
          setSuggestedSkills(skillTitles);
          
          showNotification(
            skillsData.message || `✅ ${skillTitles.length} habilidades encontradas para "${newCargo}"`, 
            "success"
          );
          
          console.log(`✅ Habilidades cargadas:`, skillTitles);
        } else {
          setSuggestedSkills([]);
          
          const message = skillsData?.message || `No se encontraron habilidades específicas para "${newCargo}"`;
          showNotification(message, "info");
          
          console.log(`ℹ️ Sin habilidades para "${newCargo}":`, skillsData);
        }
        
      } catch (error) {
        console.error(`Error obteniendo habilidades para "${newCargo}":`, error);
        setSkillsError(`Error: ${error.message}`);
        setSuggestedSkills([]);
        showNotification(
          `Error al obtener habilidades para "${newCargo}": ${error.message}`, 
          "error"
        );
      } finally {
        setLoadingSkills(false);
      }
    } else {
      // Limpiar cuando no hay cargo
      setSuggestedSkills([]);
      setSkillsError(null);
    }
  };

  // Nueva función para buscar ocupaciones a medida que el usuario escribe
  const handleOccupationSearch = async (event, value) => {
    console.log(`💡 handleOccupationSearch - value:`, value);
    if (value && value.trim().length > 2) { // Buscar solo si hay al menos 3 caracteres
      setLoadingSkills(true); // Reutilizamos loadingSkills para indicar carga de ocupaciones
      setSkillsError(null);
      try {
        const occupationsData = await escoService.searchOccupationsMultipleTerms(value.trim());
        setSuggestedOccupations(occupationsData);
      } catch (error) {
        console.error("Error al buscar ocupaciones:", error);
        setSkillsError("Error al buscar ocupaciones.");
        setSuggestedOccupations([]);
      } finally {
        setLoadingSkills(false);
      }
    } else {
      setSuggestedOccupations([]);
    }
  };

  // Función para probar manualmente (puedes llamarla desde consola)
  const debugEscoSearch = async (term) => {
    console.log(`Iniciando debug para: "${term}"`);
    try {
      const result = await escoService.debugSearch(term);
      console.log(`Resultado debug:`, result);
      return result;
    } catch (error) {
      console.error(`Error en debug:`, error);
      return { error: error.message };
    }
  };

  // Exponer función de debug globalmente en desarrollo
  if (process.env.NODE_ENV === 'development') {
    window.debugEscoSearch = debugEscoSearch;
  }

  // Agregar también esta función helper para probar términos específicos
  const testSpecificTerms = async () => {
    const testTerms = [
      'desarrollador web',
      'programador',
      'ingeniero de software',
      'contador',
      'médico',
      'abogado',
      'chef',
      'diseñador'
    ];
    
    console.log('Iniciando test de términos específicos...');
    
    for (const term of testTerms) {
      console.log(`\n Probando: "${term}"`);
      try {
        const result = await escoService.getSkillsForPosition(term);
        console.log(`"${term}": ${result.skills?.length || 0} habilidades`);
      } catch (error) {
        console.log(`"${term}": Error - ${error.message}`);
      }
      
      // Pausa entre tests
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    
    console.log('Test completado');
  };

  // Exponer función de test en desarrollo
  if (process.env.NODE_ENV === 'development') {
    window.testEscoTerms = testSpecificTerms;
  }
  
  // Función para aplicar habilidades sugeridas
  const applySuggestedSkills = () => {
    if (suggestedSkills.length > 0) {
      // Tomar las primeras 6 habilidades como sugerencia inicial
      const skillsToApply = suggestedSkills.slice(0, 6);
      handleInputChange("habilidadesDestacadas", skillsToApply);
      showNotification(`Se aplicaron ${skillsToApply.length} habilidades sugeridas`, "success");
    }
  };

  // Combinar habilidades existentes con las sugeridas para el Autocomplete
  const combinedSkillsList = useMemo(() => {
    return [...new Set(suggestedSkills)]; // Ahora solo usa las sugeridas y remueve duplicados
  }, [suggestedSkills]);

  const handleClearForm = () => {
    setFormData({
      nombreCompleto: "",
      numIdentificacion: "",
      correo: "",
      fechaNacimiento: "",
      edad: "",
      genero: null, // Cambiado de "" a null
      direccion: "",
      nivelEducativo: null, // Cambiado de "" a null
      experienciaLaboral: null, // Cambiado de "" a null
      habilidadesDestacadas: [],
      motivacionCargo: "",
      cargoAplicado: null, // Cambiado de "" a null
      ubicacionGeografica: "",
      pais: "Colombia",
    });
    setEditingCandidateId(null);
    setSuggestedSkills([]); // Limpiar habilidades sugeridas
    setSkillsError(null);   // Limpiar errores
    setSuggestedOccupations([]); // Limpiar ocupaciones sugeridas
  };

  const handleSubmitIndividual = async () => {
    try {
      setFormSubmitting(true); // Cambiar a formSubmitting

      // Validar campos requeridos
      const requiredFields = [
        { field: "nombreCompleto", label: "Nombre Completo" },
        { field: "numIdentificacion", label: "Num Identificación" },
        { field: "correo", label: "Correo" },
      ];

      const missingFields = requiredFields.filter(({ field }) => !formData[field].trim());

      if (missingFields.length > 0) {
        const missingLabels = missingFields.map(({ label }) => label).join(", ");
        showNotification(`Por favor completa los campos requeridos: ${missingLabels}`, "error");
        return;
      }

      // Validar email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.correo)) {
        showNotification("Por favor ingresa un correo electrónico válido", "error");
        return;
      }

      let resultCandidate;
      if (editingCandidateId) {
        resultCandidate = await candidateService.updateCandidate(editingCandidateId, formData);
        showNotification("Candidato editado exitosamente", "success");
      } else {
        resultCandidate = await candidateService.createCandidate(formData);
        showNotification("Candidato creado exitosamente", "success");
      }

      // Re-fetch todos los candidatos para obtener el estado más reciente y manipular el orden en el frontend.
      const latestCandidates = await candidateService.getAllCandidates();

      let finalSortedCandidates = [];
      if (resultCandidate) {
          // Filtrar el candidato afectado de la lista (si existe, para evitar duplicados si la creación lo devuelve con un nuevo ID)
          const otherCandidates = latestCandidates.filter(c => c.id_candidato !== resultCandidate.id_candidato);
          
          // Ordenar el resto de los candidatos por updated_at (descendente)
          const sortedOtherCandidates = otherCandidates.sort((a, b) => {
              const dateA = new Date(a.updated_at);
              const dateB = new Date(b.updated_at);
              return dateB.getTime() - dateA.getTime();
          });

          // Colocar el candidato afectado al principio y luego el resto ordenado
          finalSortedCandidates = [resultCandidate, ...sortedOtherCandidates];
      } else {
          // Fallback: Si no se pudo obtener el candidato afectado (no debería pasar), simplemente ordenar por updated_at.
          finalSortedCandidates = latestCandidates.sort((a, b) => {
              const dateA = new Date(a.updated_at);
              const dateB = new Date(b.updated_at);
              return dateB.getTime() - dateA.getTime();
          });
      }

      setAllCandidates(finalSortedCandidates);
      setCurrentPage(1); // Ir a la primera página para mostrar el elemento actualizado.

      // Limpiar formulario
      handleClearForm();

    } catch (error) {
      const action = editingCandidateId ? 'actualizar' : 'crear';
      showNotification(error.message || `Error al ${action} candidato`, "error");
    } finally {
      setFormSubmitting(false); // Cambiar a formSubmitting
    }
  };
  
  const handleEdit = async (candidateId) => {
    try {
      setLoading(true);
      const candidate = await candidateService.getCandidateById(candidateId);
      if (candidate) {
        const formattedData = candidateService.transformAPIDataToForm(candidate);
        setFormData(formattedData);
        setEditingCandidateId(candidateId);
        showNotification("Candidato cargado para edición", "info");
        // Hacer scroll al formulario
        if (formRef.current) {
          formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    } catch (error) {
      showNotification(error.message || "Error al cargar candidato", "error");
    } finally {
      setLoading(false); // Solo aquí debe estar
    }
  };

  const [formSubmitting, setFormSubmitting] = useState(false);


  const handleDelete = (candidateId) => {
    const candidate = allCandidates.find(c => c.id_candidato === candidateId);
    if (candidate) {
      setCandidateToDelete(candidate);
      setDeleteModalOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!candidateToDelete) return;

    try {
      setLoading(true);
      await candidateService.deleteCandidate(candidateToDelete.id_candidato);
      await loadCandidates();
      showNotification("Candidato eliminado exitosamente", "success");
    } catch (error) {
      showNotification(error.message || "Error al eliminar candidato", "error");
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
      setCandidateToDelete(null);
    }
  };

  const handleViewDetails = (candidateId) => {
    const candidate = allCandidates.find(c => c.id_candidato === candidateId);
    if (candidate) {
      setSelectedCandidate(candidate);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCandidate(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Filtrar candidatos basándose en el searchTerm
  const filteredCandidates = useMemo(() => {
    if (!searchTerm) {
      return allCandidates;
    }
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return allCandidates.filter((candidate) => {
      // Crear una función helper para verificar si un campo contiene el término
      const fieldContains = (field) => {
        return field && typeof field === 'string' && field.toLowerCase().includes(lowercasedSearchTerm);
      };
      
      // Verificar cada campo usando los nombres correctos de la base de datos
      const matchNombre = fieldContains(candidate.nombre_completo);
      const matchDocumento = fieldContains(candidate.documento?.toString());
      const matchEmail = fieldContains(candidate.email);
      const matchGenero = fieldContains(candidate.genero);
      const matchNivelEducativo = fieldContains(candidate.nivel_educativo) || fieldContains(candidate.nivelEducativo);
      const matchCargoAplicado = fieldContains(candidate.cargo_aplicado);
      const matchExperiencia = fieldContains(candidate.experiencia_laboral);
      
      return matchNombre || matchDocumento || matchEmail || matchGenero || 
             matchNivelEducativo || matchCargoAplicado || matchExperiencia;
    });
  }, [allCandidates, searchTerm]);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Paginación y formateo de datos para la tabla
  const { paginatedCandidates, totalItems, formattedRows } = useMemo(() => {
    const total = filteredCandidates.length;
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    const paginated = filteredCandidates.slice(start, end);

    const rows = paginated.map((candidate) => ({
      nombre: <TextCell value={candidate.nombre_completo} />,
      documento: <TextCell value={candidate.documento} />,
      email: <TextCell value={candidate.email} />,
      edad: <TextCell value={candidate.edad} />,
      genero: <TextCell value={candidate.genero} />,
      nivel_educativo: <TextCell value={candidate.nivel_educativo} />,
      experiencia_laboral: <TextCell value={candidate.experiencia_laboral} />,
      cargo_aplicado: <TextCell value={candidate.cargo_aplicado} />,
      acciones: <ActionsCell candidateId={candidate.id_candidato} onEdit={handleEdit} onDelete={handleDelete} onViewDetails={handleViewDetails} />,
    }));

    return { paginatedCandidates: paginated, totalItems: total, formattedRows: rows };
  }, [filteredCandidates, currentPage]);

  useEffect(() => {
    setTableData({
      columns: tableColumns,
      rows: formattedRows
    });
  }, [formattedRows]);

  const handleDownloadTemplate = () => {
    const headers = [
      "Nombre Completo",
      "Num Identificación",
      "Correo",
      "Fecha Nacimiento",
      "Edad",
      "Genero",
      "Direccion",
      "Nivel Educativo",
      "Experiencia laboral",
      "Habilidades Destacadas",
      "Motivacion por el Cargo",
      "Cargo Aplicado",
      "Ubicacion Geografica",
      "Pais",
    ];

    const ws = XLSX.utils.aoa_to_sheet([headers]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Plantilla");
    XLSX.writeFile(wb, "Plantilla_Candidatos.xlsx");

    showNotification("Plantilla descargada correctamente", "success");
  };
  //la funcion handleFileUpload es el punto de entrada cuando el usuario selecciona un archivo de Excel para cargar candidatos.
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tipo de archivo
    const validExtensions = ['.xlsx', '.xls'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
  
    if (!validExtensions.includes(fileExtension)) {
    showNotification("Por favor selecciona un archivo Excel válido (.xlsx o .xls)", "error");
    event.target.value = '';
    return;
  }
    // Leer archivo con FileReader
    const reader = new FileReader();
    reader.onload = async (e) => {
    try {
      setLoading(true);
      showNotification("Procesando archivo Excel...", "info");

      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' }); //usamos la librería XLSX para leer el contenido del archivo Excel.
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // Leer los datos con opciones específicas para fechas
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        raw: false,
        dateNF: 'yyyy-mm-dd'
      });

      if (jsonData.length === 0) {
        showNotification("El archivo está vacío o no tiene datos válidos", "warning");
        event.target.value = '';
        return;
      }

      // Validar que el archivo tenga las columnas esperadas
      const requiredColumns = ["Nombre Completo", "Num Identificación", "Correo"];
      const firstRow = jsonData[0];
      const missingColumns = requiredColumns.filter(col => !(col in firstRow));
      
      if (missingColumns.length > 0) {
        showNotification(
          `El archivo no tiene las columnas requeridas: ${missingColumns.join(', ')}. Por favor descarga la plantilla correcta.`,
          "error"
        );
        event.target.value = '';
        return;
      }

      console.log(`Archivo válido. Procesando ${jsonData.length} registros...`);

      // Procesar las fechas antes de enviar
      const processedData = processExcelData(jsonData);

      // Usar el servicio para carga masiva
      const result = await candidateService.createBulkCandidates(processedData);

      // Guardar resultados para mostrar en el modal
      setBulkUploadResults(result);
      setResultsModalOpen(true);

      // Recargar la tabla
      await loadCandidates();

      // Limpiar el input de archivo
      event.target.value = '';

      // Mostrar notificación resumida
      if (result.errors.length === 0 && (!result.duplicates || result.duplicates.length === 0)) {
        showNotification(
          `✅ Carga exitosa: ${result.success} candidatos agregados`,
          "success"
        );
      } else if (result.success === 0) {
        showNotification(
          `No se pudo cargar ningún candidato. Revisa los detalles.`,
          "error"
        );
      } else {
        const duplicatesMsg = result.duplicates?.length > 0 
          ? `, ${result.duplicates.length} duplicados` 
          : '';
        showNotification(
          `⚠️ Carga parcial: ${result.success} exitosos${duplicatesMsg}, ${result.errors.length} errores`,
          "warning"
        );
      }

    } catch (error) {
      console.error("Error al procesar el archivo:", error);
      showNotification(error.message || "Error al procesar el archivo", "error");
      event.target.value = '';
    } finally {
      setLoading(false);
    }
  };

  reader.onerror = () => {
    showNotification("Error al leer el archivo", "error");
    event.target.value = '';
    setLoading(false);
  };

  reader.readAsArrayBuffer(file);
  };

  // Función para cerrar el modal de resultados
  const handleCloseResultsModal = () => {
  setResultsModalOpen(false);
  setBulkUploadResults(null);
  };

  // Opciones para los campos select
  const generos = ["Masculino", "Femenino", "Otro"];
  const nivelesEducativos = ["Bachiller", "Técnico", "Tecnólogo", "Profesional", "Especialización", "Maestría", "Doctorado"];
  const experienciaLaboral = ["Sin experiencia", "Menos de 1 año", "1-3 años", "3-5 años", "Más de 5 años"];
  const areasSoftware = ["Frontend", "Backend", "Full-Stack", "QA / Testing", "DevOps", "Analista"];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          {/* Formulario de Carga de Candidatos */}
          <Grid item xs={12}>
            <Card ref={formRef}> {/* Asignar la ref al Card que contiene el formulario */}
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Cargar  y/o Crear Candidato 
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                <Grid container spacing={3}>
                  {/* Carga Masiva y Generación de Plantilla */}
                  <Grid item xs={12} container spacing={3} alignItems="flex-end">
                    {/* Sección Carga Masiva */}
                    <Grid item xs={12} md={7}>
                      <MDTypography variant="h6" mb={1}>CARGAR TU PLANTILLA MASIVA DE CANDIDATOS</MDTypography>
                      <MDInput
                        type="file"
                        fullWidth
                        accept=".xlsx,.xls"
                        onChange={handleFileUpload}
                        disabled={loading}
                      />
                    </Grid>
                    {/* Sección Generar Plantilla */}
                    <Grid item xs={12} md={5}>
                      <MDTypography variant="h6" mb={1}>DESCARGA TU  PLANTILLA</MDTypography>
                      <MDButton
                        variant="gradient"
                        color="info"
                        fullWidth
                        onClick={handleDownloadTemplate}
                        disabled={loading}
                      >
                        <Icon>download</Icon>&nbsp; Descargar Plantilla
                      </MDButton>
                    </Grid>
                  </Grid>

                  {/* Formulario Manual */}
                  <Grid item xs={12}>
                    <MDTypography variant="h6">CREAR CANDIDATO INDIVIDUAL</MDTypography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      label="Nombre Completo *"
                      fullWidth
                      value={formData.nombreCompleto}
                      onChange={(e) => handleInputChange("nombreCompleto", e.target.value)}
                      disabled={formSubmitting} // Cambiar a formSubmitting
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      label="Num Identificación *"
                      fullWidth
                      value={formData.numIdentificacion}
                      onChange={(e) => handleInputChange("numIdentificacion", e.target.value)}
                      inputProps={{ maxLength: 10 }}
                      helperText="Máximo 10 dígitos"
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      label="Correo *"
                      type="email"
                      fullWidth
                      value={formData.correo}
                      onChange={(e) => handleInputChange("correo", e.target.value)}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      label="Fecha de Nacimiento"
                      type="date"
                      fullWidth
                      value={formData.fechaNacimiento}
                      onChange={(e) => handleInputChange("fechaNacimiento", e.target.value)}
                      disabled={loading}
                      InputLabelProps={{ shrink: true }} // Agregado para forzar el label flotante
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      label="Edad"
                      type="number"
                      fullWidth
                      value={formData.edad}
                      onChange={(e) => handleInputChange("edad", e.target.value)}
                      disabled={loading || !!formData.fechaNacimiento}
                      helperText={formData.fechaNacimiento}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={generos}
                      value={formData.genero}
                      onChange={(event, newValue) => handleInputChange("genero", newValue || null)}
                      disabled={loading}
                      renderInput={(params) => (
                        <MDInput {...params} label="Género" fullWidth />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDInput
                      label="Dirección"
                      fullWidth
                      value={formData.direccion}
                      onChange={(e) => handleInputChange("direccion", e.target.value)}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={nivelesEducativos}
                      value={formData.nivelEducativo}
                      onChange={(event, newValue) => handleInputChange("nivelEducativo", newValue || null)}
                      disabled={loading}
                      isOptionEqualToValue={(option, value) => option === value}
                      renderInput={(params) => (
                        <MDInput {...params} label="Nivel Educativo" fullWidth />
                      )}
                    />
                  </Grid>
                  {/* START: Campos de Ubicación Geográfica y País */}
                  <Grid item xs={12} md={4}>
                    <MDInput
                      label="Ubicación Geográfica"
                      fullWidth
                      value={formData.ubicacionGeografica}
                      onChange={(e) => handleInputChange("ubicacionGeografica", e.target.value)}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      label="País"
                      fullWidth
                      value={formData.pais}
                      onChange={(e) => handleInputChange("pais", e.target.value)}
                      disabled={loading}
                    />
                  </Grid>
                  {/* END: Campos de Ubicación Geográfica y País */}
                  {/* START: Reordenamiento de campos Cargo Aplicado, Habilidades Destacadas, Experiencia Laboral y Motivación por el Cargo */}
                  <Grid item xs={12} md={6}> {/* 1. Cargo Aplicado */}
                    <Autocomplete
                      options={suggestedOccupations.map(occ => occ.title)} // Usar las ocupaciones sugeridas
                      value={formData.cargoAplicado}
                      onChange={(event, newValue) => handleCargoChange(newValue || null)}
                      onInputChange={handleOccupationSearch} // Añadido para buscar al escribir
                      disabled={loading}
                      freeSolo // Permite ingresar valores que no estén en las opciones
                      getOptionLabel={(option) => option} // Cómo mostrar la opción
                      isOptionEqualToValue={(option, value) => option === value}
                      filterOptions={(x) => x} // Desactivar el filtro interno de Autocomplete
                      renderInput={(params) => {
                        console.log(`💡 helperText render - loadingSkills:`, loadingSkills, `skillsError:`, skillsError);
                        return (
                          <MDInput 
                            {...params} 
                            label="Cargo Aplicado" 
                            fullWidth 
                            helperText={loadingSkills ? "Buscando cargos..." : skillsError ? skillsError : "Escribe al menos 3 caracteres para buscar cargos"} // Mejorado el helperText
                          />
                        );
                      }}
                    />
                  </Grid>
                  {/* 2. Panel y Campo Habilidades Destacadas */}
                  {suggestedSkills.length > 0 && (
                    <Grid item xs={12}> {/* Panel de sugerencias */}
                      <MDBox 
                        p={2} 
                        mt={1} 
                        borderRadius="lg" 
                        sx={{ 
                          backgroundColor: theme => theme.palette.grey[100],
                          border: '1px solid',
                          borderColor: theme => theme.palette.info.main
                        }}
                      >
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <MDTypography variant="subtitle2" color="info">
                            🎯 Habilidades sugeridas para {formData.cargoAplicado}
                          </MDTypography>
                          <MDButton
                            size="small"
                            variant="outlined"
                            color="info"
                            onClick={applySuggestedSkills}
                            disabled={loading}
                          >
                            Aplicar Sugeridas
                          </MDButton>
                        </MDBox>
                        
                        {/* Mensaje explicativo */}
                        <MDBox mb={2}>
                          <MDTypography variant="caption" color="text">
                            ℹ️ Haz clic en &quot;APLICAR SUGERIDAS&quot; para agregarlas todas, o selecciónalas individualmente en el campo &quot;Habilidades Destacadas&quot;.
                          </MDTypography>
                        </MDBox>
                        
                        <MDBox display="flex" flexWrap="wrap" gap={1}>
                          {suggestedSkills.slice(0, 10).map((skill, index) => (
                            <MDBox
                              key={index}
                              px={2}
                              py={0.5}
                              borderRadius="lg"
                              color="white" 
                              sx={{
                                backgroundColor: theme => theme.palette.info.main,
                                fontSize: '0.75rem',
                                fontWeight: 500
                              }}
                            >
                              {skill}
                            </MDBox>
                          ))}
                          {suggestedSkills.length > 10 && (
                            <MDTypography variant="caption" color="text">
                              +{suggestedSkills.length - 10} más...
                            </MDTypography>
                          )}
                        </MDBox>
                        
                        {skillsError && (
                          <MDTypography variant="caption" color="error" mt={1}>
                            {skillsError}
                          </MDTypography>
                        )}
                      </MDBox>
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}> {/* Campo Habilidades Destacadas */}
                    <Autocomplete
                      multiple
                      options={combinedSkillsList}
                      value={formData.habilidadesDestacadas}
                      onChange={(event, newValue) => handleInputChange("habilidadesDestacadas", newValue || [])}
                      disabled={loading}
                      renderInput={(params) => (
                        <MDInput 
                          {...params} 
                          label={`Habilidades Destacadas ${suggestedSkills.length > 0 ? '(Incluye sugeridas)' : ''}`}
                          fullWidth 
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const isSuggested = suggestedSkills.includes(option);
                          return (
                            <Chip
                              key={index}
                              label={option}
                              {...getTagProps({ index })}
                              sx={{
                                backgroundColor: isSuggested 
                                  ? theme => theme.palette.success.main 
                                  : theme => theme.palette.grey[300],
                                color: isSuggested ? 'white' : 'inherit',
                                '& .MuiChip-deleteIcon': {
                                  color: isSuggested ? 'white' : 'inherit'
                                }
                              }}
                            />
                          );
                        })
                      }
                    />
                    
                    {/* Indicador de habilidades sugeridas */}
                    {suggestedSkills.length > 0 && (
                      <MDBox mt={1}>
                        <MDTypography variant="caption" color="success">
                          ✅ Las habilidades en verde son sugeridas por ESCO API
                        </MDTypography>
                      </MDBox>
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}> {/* 3. Experiencia Laboral */}
                    <Autocomplete
                      options={experienciaLaboral}
                      value={formData.experienciaLaboral}
                      onChange={(event, newValue) => handleInputChange("experienciaLaboral", newValue || null)}
                      disabled={loading}
                      isOptionEqualToValue={(option, value) => option === value}
                      renderInput={(params) => (
                        <MDInput {...params} label="Experiencia Laboral" fullWidth />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={12}> {/* 4. Motivación por el Cargo - Ocupa una fila completa */}
                    <MDInput
                      label="Motivación por el Cargo"
                      fullWidth
                      multiline
                      rows={3}
                      value={formData.motivacionCargo}
                      onChange={(e) => handleInputChange("motivacionCargo", e.target.value)}
                      disabled={loading}
                    />
                  </Grid>
                  {/* END: Reordenamiento de campos */}
                  <Grid item xs={12}>
                    <MDBox display="flex" justifyContent="flex-end" mt={2}>
                      <MDButton
                        variant="outlined"
                        color="secondary"
                        onClick={handleClearForm}
                        disabled={formSubmitting} // Cambiar a formSubmitting
                        sx={{ mr: 1 }}
                      >
                        Limpiar
                      </MDButton>
                      <MDButton
                        variant="gradient"
                        color="success"
                        onClick={handleSubmitIndividual}
                        disabled={formSubmitting} // Cambiar a formSubmitting
                      >
                        {formSubmitting
                          ? "Procesando..."
                          : (editingCandidateId ? "Guardar Cambios" : "Guardar Candidato")
                        }
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>

          {/* Tabla de Candidatos */}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Lista de Candidatos ({totalItems} total{filteredCandidates.length !== allCandidates.length ? `, ${filteredCandidates.length} filtrados` : ""})
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={tableData}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  canSearch={false} // Deshabilitar el buscador local de la tabla
                />
              </MDBox>
              <MDBox pt={3}>
                <Pagination
                  itemsPerPage={ITEMS_PER_PAGE}
                  totalItems={totalItems}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Modal para mostrar detalles */}
      <MDModal
        open={modalOpen}
        onClose={handleCloseModal}
        title="Detalles del Candidato"
        size="lg"
      >
        {selectedCandidate && <DetallesCandidato candidato={selectedCandidate} />}
      </MDModal>

      {/* Modal de confirmación de eliminación */}
      <MDDeleteModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        item={candidateToDelete} // Ahora se pasa como 'item'
        itemType="candidato" // Se especifica el tipo de elemento
        onDelete={handleConfirmDelete}
      />
      {/* Modal de Resultados de Carga Masiva */}
      <BulkUploadResultModal
        open={resultsModalOpen}
        onClose={handleCloseResultsModal}
        results={bulkUploadResults}
      />

      {/* Notificaciones */}
      <MDSnackbar
        color={notification.color}
        icon="notifications"
        title="Notificación"
        content={notification.message || ""} // Asegurar que content no sea undefined
        dateTime={new Date().toLocaleTimeString()} // Añadir prop dateTime
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default CargarCandidatos;